import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, Image, Video, Trash2, Copy, Search, Filter, Camera } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
import StockPhotoPicker from '@/components/media/StockPhotoPicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { value: 'products', label: 'Products' },
  { value: 'banners', label: 'Banners' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'general', label: 'General' }
];

export default function DashboardMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('general');
  const fileInputRef = useRef(null);
  const [stockOpen, setStockOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`, { headers });
      setMedia(response.data);
    } catch (error) {
      toast.error('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        await axios.post(`${API}/media/upload?category=${uploadCategory}`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setUploading(false);
    fetchMedia();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!(await themedConfirm('Delete this file? This cannot be undone.'))) return;
    
    try {
      await axios.delete(`${API}/media/${id}`, { headers });
      toast.success('File deleted');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const copyUrl = (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/media/files/${id}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.original_filename?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const isImage = (contentType) => contentType?.startsWith('image/');
  const isVideo = (contentType) => contentType?.startsWith('video/');

  const sizeHints = {
    products: '1000 x 1200 px (Portrait)',
    banners: '1920 x 600 px or 1920 x 800 px',
    gallery: '1080 x 1080 px (Square) or 1000 x 1200 px (Portrait)',
    general: 'Any size'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-media">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl" data-testid="media-title">Media Library</h1>
          <p className="text-gray-500 text-sm mt-1">{media.length} files</p>
        </div>
        <button
          type="button"
          onClick={() => setStockOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-500 text-white text-xs font-bold uppercase tracking-wider hover:shadow-lg transition-all"
          data-testid="open-stock-photos"
        >
          <Camera size={12} /> Free Stock Photos
        </button>
      </div>
      <StockPhotoPicker
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        onPick={(url) => { navigator.clipboard.writeText(url); toast.success('Photo URL copied — paste into any image field'); }}
      />

      {/* Upload Zone */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium">Upload Files</h3>
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="w-[180px]" data-testid="upload-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="upload-zone block" data-testid="upload-zone">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="file-input"
          />
          <Upload size={32} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 mb-1">
            {uploading ? 'Uploading...' : 'Click or drag files to upload'}
          </p>
          <p className="size-hint">Recommended size for {uploadCategory}: {sizeHints[uploadCategory]}</p>
        </label>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search files..."
            className="pl-10"
            data-testid="search-media"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Media Grid */}
      {filteredMedia.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map((item) => (
            <div key={item.id} className="group relative" data-testid={`media-item-${item.id}`}>
              <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                {isImage(item.content_type) ? (
                  <img 
                    src={`${API}/media/files/${item.id}`}
                    alt={item.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : isVideo(item.content_type) ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <Video size={32} className="text-gray-400" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image size={32} className="text-gray-400" />
                  </div>
                )}
              </div>

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button 
                  onClick={() => copyUrl(item.id)}
                  className="w-8 h-8 bg-white rounded flex items-center justify-center hover:bg-gray-100"
                  title="Copy URL"
                  data-testid={`copy-url-${item.id}`}
                >
                  <Copy size={14} />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="w-8 h-8 bg-white rounded flex items-center justify-center hover:bg-red-50 hover:text-[#E63946]"
                  title="Delete"
                  data-testid={`delete-media-${item.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* File Info */}
              <div className="mt-2">
                <p className="text-xs truncate" title={item.original_filename}>
                  {item.original_filename}
                </p>
                <p className="text-xs text-gray-500">{item.category}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="dashboard-card text-center py-16">
          <Image size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg mb-2">No media files yet</h3>
          <p className="text-gray-500 text-sm">Upload images and videos to get started.</p>
        </div>
      )}
    </div>
  );
}
